import { useEffect, useRef, useState } from 'react';
import { Controller, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Alert, Box, Grid, Typography } from '@mui/material';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import {
  CheckboxInput,
  CtaButton,
  SelectInput,
  TextInput,
} from '@tassomutuo-backoffice/ui';
import { CreateMortgageLeadTassoCommand } from '@tassomutuo-backoffice/types';

import { CapAutocompleteInput } from '../../../components';
import AcquisitionConfirmDialog from '../acquisition/AcquisitionConfirmDialog';
import AcquisitionFormActions from '../acquisition/AcquisitionFormActions';
import AcquisitionFormSection from '../acquisition/AcquisitionFormSection';
import {
  AcquisitionActivityStore,
  useAcquisitionActivityReport,
} from '../acquisition/acquisitionActivity';
import { AcquisitionSessionStore } from '../acquisition/acquisitionSessions';
import { createMortgageLeadTasso } from '../../../services/api/mortgage-leads/mortgage-leads';
import { submitMortgageLeadTassoWithRecovery } from './leadTassoDuplicateRetry';
import { describeLeadTassoFailure } from './leadTassoErrors';
import {
  acquisitionAmountInput,
  acquisitionAmountToNumber,
} from '../acquisition/acquisitionAmount';
import {
  ACQUISITION_EMAIL_PATTERN,
  acquisitionNameRule,
} from '../acquisition/acquisitionCustomer';
import { leadTassoLoanToValueLabel } from './leadTassoAmount';
import {
  buildLeadTassoCommand,
  LEAD_TASSO_DEFAULT_VALUES,
  LeadTassoFormValues,
} from './leadTassoForm';
import {
  LEAD_TASSO_REVIEW_NOTICE,
  LEAD_TASSO_REVIEW_TITLE,
  leadTassoReviewSections,
} from './leadTassoReview';
import {
  LEAD_TASSO_DURATION_OPTIONS,
  LEAD_TASSO_PURPOSE_OPTIONS,
  LEAD_TASSO_RATE_TYPE_OPTIONS,
  LEAD_TASSO_UNDER36_OPTIONS,
} from './leadTassoOptions';
import {
  LEAD_TASSO_PHONE_PATTERN,
  leadTassoAmountRule,
  leadTassoLoanToValueRule,
} from './leadTassoValidation';
import AcquisitionPhonePrefixSelect from '../acquisition/AcquisitionPhonePrefixSelect';

const SUCCESS_MESSAGE = 'Lead acquisita; elaborazione in corso';

const PRODUCT = 'mutui' as const;

interface IProps {
  /** Where this product's unfinished draft and command identity outlive the tab. */
  sessions: AcquisitionSessionStore;
  /** What the workspace is told about this compilation while it is mounted. */
  activity: AcquisitionActivityStore;
}

const LeadTassoMutuoPanel = ({ sessions, activity }: IProps) => {
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  /**
   * The command under review. Submitting the form compiles it once, and both
   * the review and the confirmed request read this same object: what the
   * operator approves is what leaves the browser, and a form edited behind the
   * review cannot change it.
   */
  const [reviewedCommand, setReviewedCommand] =
    useState<CreateMortgageLeadTassoCommand | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  // A double click or a repeated key activation fires two confirmations
  // before `mutation.isPending` re-renders the disabled button; this ref
  // closes that gap so only one request per activation ever goes out.
  const isSubmittingRef = useRef(false);

  const {
    control,
    getValues,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<LeadTassoFormValues>({
    defaultValues:
      sessions.getDraft<LeadTassoFormValues>(PRODUCT) ??
      LEAD_TASSO_DEFAULT_VALUES,
  });

  // Leaving the tab unmounts this panel; an unfinished compilation is handed
  // back so the operator returns to it rather than to an empty form. A clean
  // form has nothing to hand back, and saving it anyway would overwrite the
  // cleared draft of a product that has just been acquired.
  const saveDraftRef = useRef<() => void>();
  saveDraftRef.current = () => {
    if (isDirty) sessions.setDraft(PRODUCT, getValues());
  };
  useEffect(() => () => saveDraftRef.current?.(), []);

  const [propertyValue, loanAmount] = useWatch({
    control,
    name: ['propertyValue', 'loanAmount'],
  });
  const loanToValueLabel = leadTassoLoanToValueLabel(loanAmount, propertyValue);

  const mutation = useMutation({
    mutationFn: (command: CreateMortgageLeadTassoCommand) =>
      submitMortgageLeadTassoWithRecovery(() =>
        createMortgageLeadTasso(command, sessions.getIdempotencyKey(PRODUCT))
      ),
    onSuccess: () => {
      setSubmissionError(null);
      toast.success(SUCCESS_MESSAGE);
      reset(LEAD_TASSO_DEFAULT_VALUES);
      // The compilation is over: the next one is a new command, never a
      // replay of the lead that was just acquired.
      sessions.reset(PRODUCT);
    },
    onError: (error: unknown) => {
      setSubmissionError(describeLeadTassoFailure(error));
    },
    onSettled: () => {
      isSubmittingRef.current = false;
      // A success is announced by a toast and a failure on the page, above the
      // form the operator may need to correct: the review never stays in the
      // way of either.
      setReviewedCommand(null);
    },
  });

  // The tabs above lock while this request is out, and an unfinished draft is
  // worth a question before the route changes: both are the workspace's to do,
  // and neither can be seen from there once this panel unmounts.
  useAcquisitionActivityReport(activity, PRODUCT, {
    busy: mutation.isPending,
    unsaved: isDirty,
  });

  /** Submitting only opens the review; it never reaches the API. */
  const onSubmit: SubmitHandler<LeadTassoFormValues> = (data) => {
    if (mutation.isPending) return;
    setReviewedCommand(buildLeadTassoCommand(data));
  };

  const onInvalid = () => {
    // React Hook Form focuses the first invalid registered input; the selects
    // and the location autocomplete are not always focusable, so the first
    // field flagged invalid is brought into view as well.
    window.requestAnimationFrame(() => {
      formRef.current
        ?.querySelector<HTMLElement>('[aria-invalid="true"]')
        ?.scrollIntoView?.({ block: 'center' });
    });
  };

  const confirmAcquisition = () => {
    if (isSubmittingRef.current || !reviewedCommand) return;
    isSubmittingRef.current = true;
    setSubmissionError(null);
    mutation.mutate(reviewedCommand);
  };

  return (
    <>
      <Typography color="text.secondary" mb={3}>
        Registra una lead mutuo. La lead entra centralmente in Programma Tasso
        come Lead in gestione.
      </Typography>

      {submissionError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {submissionError}
        </Alert>
      )}

      <Box
        component="form"
        ref={formRef}
        onSubmit={handleSubmit(onSubmit, onInvalid)}
        noValidate
      >
        <fieldset
          disabled={mutation.isPending}
          style={{ border: 0, padding: 0, margin: 0 }}
        >
          <AcquisitionFormSection
            title="Informazioni mutuo"
            description="Finalità, importi e preferenze del finanziamento."
            icon={<HomeOutlinedIcon />}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="purpose"
                  rules={{ required: 'Seleziona la finalità del mutuo' }}
                  render={({ field }) => (
                    <SelectInput
                      label="Finalità del mutuo"
                      selectOptions={LEAD_TASSO_PURPOSE_OPTIONS}
                      error={!!errors.purpose}
                      helperText={errors.purpose?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="durationYears"
                  rules={{ required: 'Seleziona la durata del mutuo' }}
                  render={({ field }) => (
                    <SelectInput
                      label="Durata (anni)"
                      selectOptions={LEAD_TASSO_DURATION_OPTIONS}
                      error={!!errors.durationYears}
                      helperText={errors.durationYears?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="propertyValue"
                  rules={{
                    required: "Inserisci il valore dell'immobile",
                    deps: 'loanAmount',
                    validate: (value) =>
                      leadTassoAmountRule(acquisitionAmountToNumber(value)),
                  }}
                  render={({ field }) => (
                    <TextInput
                      label="Valore immobile"
                      showEuroIcon
                      error={!!errors.propertyValue}
                      helperText={errors.propertyValue?.message}
                      {...field}
                      onChange={(e: { target: { value: string } }) =>
                        field.onChange(acquisitionAmountInput(e.target.value))
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="loanAmount"
                  rules={{
                    required: "Inserisci l'importo richiesto",
                    validate: (value, { propertyValue }) => {
                      const amount = acquisitionAmountToNumber(value);
                      const amountResult = leadTassoAmountRule(amount);
                      return amountResult === true && amount !== null
                        ? leadTassoLoanToValueRule(
                            amount,
                            acquisitionAmountToNumber(propertyValue)
                          )
                        : amountResult;
                    },
                  }}
                  render={({ field }) => (
                    <TextInput
                      label="Importo richiesto"
                      showEuroIcon
                      error={!!errors.loanAmount}
                      helperText={errors.loanAmount?.message}
                      {...field}
                      onChange={(e: { target: { value: string } }) =>
                        field.onChange(acquisitionAmountInput(e.target.value))
                      }
                    />
                  )}
                />
                {loanToValueLabel && (
                  <Typography variant="body2" color="text.secondary" pl={1}>
                    {loanToValueLabel} del valore dell&apos;immobile
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="under36Choice"
                  rules={{ required: 'Indica se il cliente è under 36' }}
                  render={({ field }) => (
                    <SelectInput
                      label="Cliente under 36"
                      selectOptions={LEAD_TASSO_UNDER36_OPTIONS}
                      error={!!errors.under36Choice}
                      helperText={errors.under36Choice?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="rateType"
                  rules={{ required: 'Seleziona il tasso preferito' }}
                  render={({ field }) => (
                    <SelectInput
                      label="Tasso preferito"
                      selectOptions={LEAD_TASSO_RATE_TYPE_OPTIONS}
                      error={!!errors.rateType}
                      helperText={errors.rateType?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </AcquisitionFormSection>

          <AcquisitionFormSection
            title="Informazioni cliente"
            description="Dati anagrafici e recapiti del cliente."
            icon={<PersonOutlineIcon />}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="firstName"
                  rules={{
                    required: 'Inserisci il nome',
                    validate: acquisitionNameRule,
                  }}
                  render={({ field }) => (
                    <TextInput
                      label="Nome"
                      error={!!errors.firstName}
                      helperText={errors.firstName?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="lastName"
                  rules={{
                    required: 'Inserisci il cognome',
                    validate: acquisitionNameRule,
                  }}
                  render={({ field }) => (
                    <TextInput
                      label="Cognome"
                      error={!!errors.lastName}
                      helperText={errors.lastName?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="email"
                  rules={{
                    required: "Inserisci l'email",
                    pattern: {
                      value: ACQUISITION_EMAIL_PATTERN,
                      message: 'Inserisci un indirizzo email valido',
                    },
                  }}
                  render={({ field }) => (
                    <TextInput
                      label="Email"
                      type="email"
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <Controller
                  control={control}
                  name="phoneCountry"
                  render={({ field }) => (
                    <AcquisitionPhonePrefixSelect
                      id="lead-tasso-phone-prefix"
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <Controller
                  control={control}
                  name="phone"
                  rules={{
                    required: 'Inserisci il telefono',
                    pattern: {
                      value: LEAD_TASSO_PHONE_PATTERN,
                      message: '5–15 cifre, senza spazi',
                    },
                  }}
                  render={({ field }) => (
                    <TextInput
                      label="Telefono"
                      error={!!errors.phone}
                      helperText={errors.phone?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </AcquisitionFormSection>

          <AcquisitionFormSection
            title="Località"
            description="Comune dell'immobile o di residenza del cliente."
            icon={<PlaceOutlinedIcon />}
          >
            <Box maxWidth={400}>
              <Controller
                control={control}
                name="location"
                rules={{
                  validate: (value) =>
                    !!value ||
                    "Seleziona una località dai risultati dell'autocomplete",
                }}
                render={({ field, fieldState }) => (
                  <CapAutocompleteInput
                    id="lead-tasso-location"
                    label="Cerca per CAP o comune"
                    value={field.value}
                    onChange={(option) => field.onChange(option?.value ?? null)}
                    errorMessage={fieldState.error?.message}
                  />
                )}
              />
            </Box>
          </AcquisitionFormSection>

          <AcquisitionFormSection
            title="Comunicazione"
            description="Messaggi inviati al cliente dopo l'acquisizione."
            icon={<MailOutlineIcon />}
          >
            <Box>
              <Controller
                control={control}
                name="sendWelcomeCommunication"
                render={({ field }) => (
                  <CheckboxInput
                    label="Invia comunicazione di benvenuto al cliente"
                    aria-describedby="lead-tasso-welcome-helper"
                    {...field}
                  />
                )}
              />
              <Typography
                id="lead-tasso-welcome-helper"
                variant="body2"
                color="text.secondary"
              >
                Controlla soltanto il primo messaggio al cliente. La successiva
                comunicazione sul consulente assegnato viene sempre inviata.
              </Typography>
            </Box>
          </AcquisitionFormSection>

          <AcquisitionFormActions>
            <CtaButton
              variant="contained"
              type="submit"
              loading={mutation.isPending}
              disabled={mutation.isPending}
            >
              Registra Lead Tasso
            </CtaButton>
          </AcquisitionFormActions>
        </fieldset>
      </Box>

      {reviewedCommand && (
        <AcquisitionConfirmDialog
          open
          title={LEAD_TASSO_REVIEW_TITLE}
          notice={LEAD_TASSO_REVIEW_NOTICE}
          sections={leadTassoReviewSections(reviewedCommand)}
          pending={mutation.isPending}
          onEdit={() => setReviewedCommand(null)}
          onConfirm={confirmAcquisition}
        />
      )}
    </>
  );
};

export default LeadTassoMutuoPanel;
